(function (globalScope, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (globalScope) {
    globalScope.EyesnugDeliveryPromise = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const TIME_ZONE = 'Europe/London';
  const BANK_HOLIDAY_DIVISION = 'england-and-wales';
  const BANK_HOLIDAY_API = 'https://www.gov.uk/bank-holidays.json';
  const BANK_HOLIDAY_CACHE_KEY = 'eyesnug_delivery_bank_holidays_v2';
  const BANK_HOLIDAY_CACHE_TTL = 7 * 24 * 60 * 60 * 1000;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const DISPATCH_CUTOFF_MINUTES = Object.freeze({
    1: 16 * 60,
    2: 16 * 60,
    3: 16 * 60,
    4: 16 * 60,
    5: 16 * 60,
    6: 12 * 60
  });

  let bankHolidayRequest;

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function toISODate(dateParts) {
    return `${dateParts.year}-${pad(dateParts.month)}-${pad(dateParts.day)}`;
  }

  function fromISODate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
    if (!match) return null;

    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3])
    };
  }

  function toDayNumber(dateParts) {
    return Math.floor(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day) / DAY_MS);
  }

  function fromDayNumber(dayNumber) {
    const date = new Date(dayNumber * DAY_MS);
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate()
    };
  }

  function addDays(dateParts, amount) {
    return fromDayNumber(toDayNumber(dateParts) + amount);
  }

  function dayOfWeek(dateParts) {
    return new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day)).getUTCDay();
  }

  function nthWeekdayOfMonth(year, month, weekday, occurrence) {
    const first = { year, month, day: 1 };
    const offset = (weekday - dayOfWeek(first) + 7) % 7;
    return { year, month, day: 1 + offset + ((occurrence - 1) * 7) };
  }

  function lastWeekdayOfMonth(year, month, weekday) {
    const firstOfNextMonth = month === 12
      ? { year: year + 1, month: 1, day: 1 }
      : { year, month: month + 1, day: 1 };
    const last = addDays(firstOfNextMonth, -1);
    const offset = (dayOfWeek(last) - weekday + 7) % 7;
    return addDays(last, -offset);
  }

  // Meeus/Jones/Butcher Gregorian Easter calculation.
  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return { year, month, day };
  }

  function addObservedChristmasHolidays(year, dates) {
    const occupied = new Set();
    const christmas = { year, month: 12, day: 25 };
    const boxingDay = { year, month: 12, day: 26 };

    [christmas, boxingDay].forEach((dateParts) => {
      const weekday = dayOfWeek(dateParts);
      if (weekday >= 1 && weekday <= 5) {
        occupied.add(toISODate(dateParts));
        dates.add(toISODate(dateParts));
      }
    });

    [christmas, boxingDay].forEach((dateParts) => {
      const weekday = dayOfWeek(dateParts);
      if (weekday !== 0 && weekday !== 6) return;

      let substitute = addDays(dateParts, 1);
      while (
        dayOfWeek(substitute) === 0 ||
        dayOfWeek(substitute) === 6 ||
        occupied.has(toISODate(substitute))
      ) {
        substitute = addDays(substitute, 1);
      }

      occupied.add(toISODate(substitute));
      dates.add(toISODate(substitute));
    });
  }

  // Statutory fallback. The GOV.UK feed is merged over this so one-off holidays
  // (for example a coronation) are still picked up when available.
  function englandAndWalesBankHolidaysForYear(year) {
    const dates = new Set();
    const newYearsDay = { year, month: 1, day: 1 };
    const newYearWeekday = dayOfWeek(newYearsDay);
    const observedNewYear = (newYearWeekday === 0 || newYearWeekday === 6)
      ? nthWeekdayOfMonth(year, 1, 1, 1)
      : newYearsDay;
    const easter = easterSunday(year);

    dates.add(toISODate(observedNewYear));
    dates.add(toISODate(addDays(easter, -2)));
    dates.add(toISODate(addDays(easter, 1)));
    dates.add(toISODate(nthWeekdayOfMonth(year, 5, 1, 1)));
    dates.add(toISODate(lastWeekdayOfMonth(year, 5, 1)));
    dates.add(toISODate(lastWeekdayOfMonth(year, 8, 1)));
    addObservedChristmasHolidays(year, dates);

    return Array.from(dates).sort();
  }

  function fallbackBankHolidays(referenceDate) {
    const referenceYear = getZonedParts(referenceDate || new Date()).year;
    const dates = [];

    for (let year = referenceYear - 1; year <= referenceYear + 3; year += 1) {
      dates.push(...englandAndWalesBankHolidaysForYear(year));
    }

    return dates;
  }

  function getZonedParts(date, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone || TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
    const parts = Object.fromEntries(
      formatter.formatToParts(date).map((part) => [part.type, part.value])
    );

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second)
    };
  }

  function civilEpoch(dateParts) {
    return Date.UTC(
      dateParts.year,
      dateParts.month - 1,
      dateParts.day,
      dateParts.hour || 0,
      dateParts.minute || 0,
      dateParts.second || 0
    );
  }

  function zonedDateTimeToDate(dateParts, timeZone) {
    const zone = timeZone || TIME_ZONE;
    const targetCivilEpoch = civilEpoch(dateParts);
    let instant = targetCivilEpoch;

    // Iteratively remove the zone offset. Dispatch cutoffs occur outside DST's
    // ambiguous early-morning hour, so this converges to one unambiguous instant.
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const actualParts = getZonedParts(new Date(instant), zone);
      const correction = targetCivilEpoch - civilEpoch(actualParts);
      instant += correction;
      if (correction === 0) break;
    }

    return new Date(instant);
  }

  function parseDateList(value) {
    if (Array.isArray(value)) {
      return value.filter((date) => fromISODate(date)).map(String);
    }

    return String(value || '')
      .split(/[\s,]+/)
      .map((date) => date.trim())
      .filter((date) => fromISODate(date));
  }

  function makeBlockedDateSet(bankHolidayDates, closureDates) {
    return new Set([
      ...parseDateList(bankHolidayDates),
      ...parseDateList(closureDates)
    ]);
  }

  function cutoffMinutesForDate(dateParts, blockedDates) {
    if (blockedDates && blockedDates.has(toISODate(dateParts))) return null;
    return DISPATCH_CUTOFF_MINUTES[dayOfWeek(dateParts)] ?? null;
  }

  function isDeliveryDay(dateParts, blockedDates) {
    const weekday = dayOfWeek(dateParts);
    return weekday >= 1 && weekday <= 6 && !(blockedDates && blockedDates.has(toISODate(dateParts)));
  }

  function findDispatchSlot(nowParts, blockedDates) {
    let dispatchDate = {
      year: nowParts.year,
      month: nowParts.month,
      day: nowParts.day
    };
    const currentMinute = (nowParts.hour * 60) + nowParts.minute + (nowParts.second / 60);
    let cutoffMinutes = cutoffMinutesForDate(dispatchDate, blockedDates);

    if (cutoffMinutes !== null && currentMinute < cutoffMinutes) {
      return { date: dispatchDate, cutoffMinutes };
    }

    do {
      dispatchDate = addDays(dispatchDate, 1);
      cutoffMinutes = cutoffMinutesForDate(dispatchDate, blockedDates);
    } while (cutoffMinutes === null);

    return { date: dispatchDate, cutoffMinutes };
  }

  function nextDeliveryDate(dispatchDate, blockedDates) {
    let arrivalDate = addDays(dispatchDate, 1);

    while (!isDeliveryDay(arrivalDate, blockedDates)) {
      arrivalDate = addDays(arrivalDate, 1);
    }

    return arrivalDate;
  }

  function calculatePromise(now, bankHolidayDates, closureDates) {
    const blockedDates = makeBlockedDateSet(bankHolidayDates, closureDates);
    const nowParts = getZonedParts(now, TIME_ZONE);
    const dispatch = findDispatchSlot(nowParts, blockedDates);
    const arrivalDate = nextDeliveryDate(dispatch.date, blockedDates);
    const cutoffHour = Math.floor(dispatch.cutoffMinutes / 60);
    const cutoffMinute = dispatch.cutoffMinutes % 60;
    const cutoffDate = zonedDateTimeToDate({
      ...dispatch.date,
      hour: cutoffHour,
      minute: cutoffMinute,
      second: 0
    }, TIME_ZONE);

    return {
      dispatchDate: toISODate(dispatch.date),
      arrivalDate: toISODate(arrivalDate),
      cutoffDate
    };
  }

  function formatArrival(isoDate) {
    const dateParts = fromISODate(isoDate);
    if (!dateParts) return '';

    const noonInLondon = zonedDateTimeToDate({
      ...dateParts,
      hour: 12,
      minute: 0,
      second: 0
    }, TIME_ZONE);

    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: TIME_ZONE
    }).format(noonInLondon);
  }

  function formatCountdown(milliseconds) {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const clock = `${days ? `${days}d ` : ''}${hours}h ${pad(minutes)}m ${pad(seconds)}s`;

    return clock.trim();
  }

  function formatDispatch(isoDate) {
    const parts = fromISODate(isoDate);
    if (!parts) return '';
    const formatted = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC'
    }).formatToParts(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)));
    const fields = Object.fromEntries(formatted.map(part => [part.type, part.value]));
    return `${fields.weekday}, ${fields.day} ${fields.month}`;
  }

  function readCachedBankHolidays() {
    try {
      const parsed = JSON.parse(localStorage.getItem(BANK_HOLIDAY_CACHE_KEY));
      if (!parsed || !Array.isArray(parsed.dates)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeCachedBankHolidays(dates) {
    try {
      localStorage.setItem(BANK_HOLIDAY_CACHE_KEY, JSON.stringify({
        savedAt: Date.now(),
        dates
      }));
    } catch (error) {
      // Storage can be unavailable in private browsing; the in-memory result
      // still keeps the current page accurate.
    }
  }

  function loadBankHolidays() {
    if (bankHolidayRequest) return bankHolidayRequest;

    bankHolidayRequest = (async function () {
      const fallbackDates = fallbackBankHolidays(new Date());
      const cached = readCachedBankHolidays();
      const cachedDates = cached ? parseDateList(cached.dates) : [];
      const cachedIsFresh = cached && (Date.now() - Number(cached.savedAt || 0)) < BANK_HOLIDAY_CACHE_TTL;

      if (cachedIsFresh) {
        return Array.from(new Set([...fallbackDates, ...cachedDates])).sort();
      }

      try {
        const response = await fetch(BANK_HOLIDAY_API, {
          credentials: 'omit',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Bank holiday request failed: ${response.status}`);

        const data = await response.json();
        const fetchedDates = (data[BANK_HOLIDAY_DIVISION]?.events || [])
          .map((event) => event.date)
          .filter((date) => fromISODate(date));
        const mergedDates = Array.from(new Set([...fallbackDates, ...fetchedDates])).sort();
        writeCachedBankHolidays(fetchedDates);
        return mergedDates;
      } catch (error) {
        return Array.from(new Set([...fallbackDates, ...cachedDates])).sort();
      }
    })();

    return bankHolidayRequest;
  }

  function countryCanShowExactArrival(countryCode) {
    const country = String(countryCode || 'GB').toUpperCase();
    return country === 'GB' || country === 'UK';
  }

  function initialiseRoot(root) {
    if (!root || root.dataset.eyesnugDeliveryInitialised === 'true') return;
    root.dataset.eyesnugDeliveryInitialised = 'true';

    if (!countryCanShowExactArrival(root.dataset.marketCountry)) {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      return;
    }

    const arrivalElement = root.querySelector('.rm24-arrives');
    const countdownElement = root.querySelector('.rm24-countdown');
    if (!arrivalElement || !countdownElement) return;

    const configuredClosures = parseDateList(root.dataset.closureDates);
    let bankHolidayDates = fallbackBankHolidays(new Date());
    let timer;

    const render = function () {
      if (typeof document !== 'undefined' && !document.documentElement.contains(root)) {
        if (timer) clearInterval(timer);
        return;
      }

      const now = new Date();
      const promise = calculatePromise(now, bankHolidayDates, configuredClosures);
      arrivalElement.textContent = formatArrival(promise.arrivalDate);
      countdownElement.textContent = formatCountdown(promise.cutoffDate.getTime() - now.getTime());
    };

    render();
    timer = setInterval(render, 1000);

    loadBankHolidays().then((dates) => {
      bankHolidayDates = dates;
      render();
    });
  }

  function initialiseDispatchRoot(root) {
    if (!root || root.dataset.eyesnugDispatchInitialised === 'true') return;
    const copy = root.querySelector('[data-dispatch-copy]');
    if (!copy) return;
    root.dataset.eyesnugDispatchInitialised = 'true';
    const dateElement = document.createElement('time');
    copy.replaceChildren(document.createTextNode('Ships by '), dateElement);
    const closures = parseDateList(root.dataset.closureDates);
    let holidays = fallbackBankHolidays(new Date());
    let timer;
    const render = function () {
      if (!document.documentElement.contains(root)) {
        if (timer) clearInterval(timer);
        return;
      }
      const { dispatchDate } = calculatePromise(new Date(), holidays, closures);
      if (dateElement.dateTime !== dispatchDate) {
        dateElement.dateTime = dispatchDate;
        dateElement.textContent = formatDispatch(dispatchDate);
      }
    };
    render();
    timer = setInterval(render, 1000);
    loadBankHolidays().then(function (dates) { holidays = dates; render(); });
  }

  function boot(scope) {
    if (typeof document === 'undefined') return;
    const target = scope && scope.querySelectorAll ? scope : document;
    target.querySelectorAll('[data-eyesnug-delivery-promise]').forEach(initialiseRoot);
    target.querySelectorAll('[data-eyesnug-dispatch-promise]').forEach(initialiseDispatchRoot);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
    } else {
      boot(document);
    }

    document.addEventListener('shopify:section:load', function (event) {
      boot(event.target);
    });
  }

  return {
    TIME_ZONE,
    addDays,
    calculatePromise,
    countryCanShowExactArrival,
    dayOfWeek,
    englandAndWalesBankHolidaysForYear,
    fallbackBankHolidays,
    formatArrival,
    formatCountdown,
    formatDispatch,
    fromISODate,
    getZonedParts,
    toISODate,
    zonedDateTimeToDate
  };
});
